import React from 'react'
import {Stack, Box, StackProps, useMultiStyleConfig, Flex, Text, TextProps, BoxProps, Grid, GridProps, Link} from '@chakra-ui/react'
import {Button, ButtonProps, Icon} from '_uiComponents'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {NewsletterFormValues, ResponseSavePreference, Step2SchemaCheckbox, Step2SchemaInputs, Step2SchemaRadio} from '_types'
import {useCustomApi} from '_customHooks'
import {FormFactory, FormValues} from '_factories'

interface NewsletterRegistrationProps {
    title?: string
    description?: string
    email: string
    localeID: string
    variant?: string
    onSubmit: (preferences: ResponseSavePreference) => void
    onLoading: (loading: boolean) => void
}

/**
 * @name NewsletterRegistrationStep2
 *
 * @component
 *
 * @description
 * Renders the step 2 of Newsletter Modal.
 *
 * @param {NewsletterRegistrationProps} props - Properties object
 * @param {string} [props.title] - Title for the locale modal.
 * @param {string} [props.description] - Description for the locale modal.
 * @param {string} [props.email] - email passed from father component
 * @param {string} props.localeID - Locale id format it-it.
 * @param {string} [props.variant] - variant mobile for styling
 * @param {function} props.onSubmit - Handle submit callback.
 * @param {function} props.onLoading - Handle loading callback.
 *
 * @returns {React.FC<NewsletterRegistrationProps>} Rendered Newsletter Modal Step 2.
 */
const NewsletterRegistrationStep2: React.FC<NewsletterRegistrationProps> = ({title, email, variant, localeID, onSubmit, onLoading}) => {
    const styles = useMultiStyleConfig('NewsletterModal_step_2', {variant}) as Record<string, StackProps & TextProps & BoxProps>
    const {formatMessage} = useIntl()
    const {useSubmitNewsletterPreferences} = useCustomApi()
    const {mutate} = useSubmitNewsletterPreferences()

    const {
        control,
        formState: {errors},
        handleSubmit,
        setError,
    } = useForm<FormValues>()

    const submitForm = (data: FormValues) => {
        const excludeKeysFromPreferences: string[] = ['name', 'surname', 'preferenceNewsletterConsent']
        const form = formObject(data, excludeKeysFromPreferences)
        form.email = email
        if ((data.preferenceNewsletterConsent === 'false' || !data.preferenceNewsletterConsent) && form.preferences.length > 0) {
            setError('preferenceNewsletterConsent', {
                type: 'manual',
                message: formatMessage(
                    {
                        id: 'label.newsletter.privacy.error',
                        defaultMessage: 'Error: Select "{0}" to customize your preferences.',
                    },
                    {0: formatMessage({id: 'profile.privacysi', defaultMessage: 'I Agree'})},
                ),
            })
        } else {
            try {
                onLoading(true)
                mutate(
                    {form: form},
                    {
                        onSuccess: (data) => {
                            onLoading(false)
                            if (data.success) {
                                onSubmit(data.filledData)
                            }
                        },
                        onError: (e) => {
                            console.error(e)
                            onLoading(false)
                        },
                    },
                )
            } catch (error) {
                console.error(error)
            }
        }
    }

    const step2SchemaInputs: Step2SchemaInputs = [
        {
            type: 'text',
            name: 'name',
            label: formatMessage({id: 'label.input.firstname.profile', defaultMessage: 'First Name'}),
            placeholder: formatMessage(
                {
                    id: 'label.field.placeholder',
                    defaultMessage: 'Enter',
                },
                {0: formatMessage({id: 'label.input.firstname.profile', defaultMessage: 'First Name'})},
            ),

            isRequired: false,
            variant: 'newsletter',
        },
        {
            type: 'text',
            name: 'surname',
            label: formatMessage({id: 'label.input.lastname.profile', defaultMessage: 'Last Name'}),
            placeholder: formatMessage(
                {
                    id: 'label.field.placeholder',
                    defaultMessage: 'Enter',
                },
                {0: formatMessage({id: 'label.input.lastname.profile', defaultMessage: 'Last Name'})},
            ),

            isRequired: false,
            variant: 'newsletter',
        },
    ]

    const step2SchemaCheckbox: Step2SchemaCheckbox = [
        {
            type: 'checkbox',
            name: 'men',
            label: formatMessage({id: 'label.newsletter.form.man', defaultMessage: 'Men'}),
            isRequired: false,
        },
        {
            type: 'checkbox',
            name: 'women',
            label: formatMessage({id: 'label.newsletter.form.women', defaultMessage: 'Women'}),
            isRequired: false,
        },
        {
            type: 'checkbox',
            name: 'kids',
            label: formatMessage({id: 'label.newsletter.form.kid', defaultMessage: 'Kids'}),
            isRequired: false,
        },
        {
            type: 'checkbox',
            name: 'eyewear',
            label: formatMessage({id: 'label.newsletter.form.eyewear', defaultMessage: 'Eyewear'}),
            isRequired: false,
        },
        {
            type: 'checkbox',
            name: 'fragrances',
            label: formatMessage({id: 'label.newsletter.form.fragrances', defaultMessage: 'Fragrances'}),
            isRequired: false,
        },
        {
            type: 'checkbox',
            name: 'lifestyle',
            label: formatMessage({id: 'label.newsletter.form.lifestyle', defaultMessage: 'Lifestyle'}),
            isRequired: false,
        },
    ]

    const radioOptions = [
        {
            value: 'true',
            label: formatMessage({id: 'profile.privacysi', defaultMessage: 'I agree'}),
        },
        {
            value: 'false',
            label: formatMessage({id: 'profile.privacyno', defaultMessage: 'I do not agree'}),
        },
    ]

    const step2SchemaRadio: Step2SchemaRadio = {
        type: 'radio',
        name: 'preferenceNewsletterConsent',
        label: '"' + formatMessage({id: 'profile.privacysi', defaultMessage: 'I agree'}) + '"',
        options: radioOptions,
        isRequired: false,
    }

    return (
        <Stack data-testid="email-input-nl-2" as="form" noValidate onSubmit={handleSubmit(submitForm)} {...styles.form} data-currentemail={email}>
            <Box {...styles.titleWrapper}>
                <Text>{title}</Text>
            </Box>
            <Flex {...styles.inputsWrapper}>
                {step2SchemaInputs.map((schema, index) => (
                    <FormFactory
                        key={`${index}_${schema.name}`}
                        control={control}
                        errors={errors}
                        name={schema.name}
                        type={schema.type}
                        label={schema.label}
                        placeholder={schema.placeholder}
                        options={schema.options}
                        defaultValue={schema.defaultValue}
                        isRequired={schema.isRequired}
                        variant="newsletter"
                    />
                ))}
            </Flex>
            <Box {...styles.description}>
                <FormattedMessage
                    id="label.newsletter.preferences.subtitle"
                    defaultMessage="Select your lines of interest to help us tailor an experience just for you."
                />
            </Box>
            <Grid {...(styles.gridPreferences as GridProps)}>
                {step2SchemaCheckbox.map((schema, index) => (
                    <FormFactory
                        key={`${index}_${schema.name}`}
                        control={control}
                        errors={errors}
                        name={schema.name}
                        type={schema.type}
                        label={schema.label}
                        id={`${index}_${schema.name}`}
                        placeholder={schema.placeholder}
                        options={schema.options}
                        defaultValue={schema.defaultValue}
                        isRequired={false}
                        size="sm"
                    />
                ))}
            </Grid>
            <Box {...styles.description}>
                <Text>
                    <FormattedMessage
                        id="label.newsletter.form.privacy.subtitle"
                        defaultMessage={`To proceed and allow us to send you customized communications, please select the option below.`}
                    />
                </Text>
                <FormattedMessage
                    id="registration.privacy.title"
                    defaultMessage={`I, the undersigned, read and understood the {privacyPolicy}.`}
                    values={{
                        privacyPolicy: (
                            <Link className="cc-privacy-link" href={`/${localeID}/legalarea`} target="_blank" rel="noreferrer">
                                {formatMessage({id: 'registration.privacy.title.link', defaultMessage: 'Privacy Policy'})}
                            </Link>
                        ),
                    }}
                />
            </Box>
            <Box {...styles.radioWrapper}>
                <FormFactory
                    control={control}
                    errors={errors}
                    name={step2SchemaRadio.name}
                    type={step2SchemaRadio.type}
                    label={step2SchemaRadio.label}
                    placeholder={step2SchemaRadio.placeholder}
                    options={step2SchemaRadio.options}
                    defaultValue={step2SchemaRadio.defaultValue}
                    isRequired={step2SchemaRadio.isRequired}
                    size="newsletter"
                />
            </Box>
            <Box {...styles.privacy_policy_2}>
                <FormattedMessage
                    id="profile.privacyshoppingbottom"
                    defaultMessage="the use of my personal details by Brunello Cucinelli S.p.A. to define my profile, in order to identify the most relevant communication and services for me."
                />
            </Box>
            <Flex {...styles.footerWrapper}>
                <Button {...(styles.button as ButtonProps)} variant="primary" size="newsletter" type="submit">
                    <Icon size="sm" name="IconEmail" />
                    <FormattedMessage id="label.button.savepreferences" defaultMessage="Save Preferences" />
                </Button>
                <Box as="span" {...styles.footerCopyrightMessage}>
                    <FormattedMessage
                        id="label.newsletter.form.policy"
                        defaultMessage={`By entering your e-mail address, you agree to receive Brunello Cucinelli communications regarding the brand’s collections, news, and special content. For more information, see our {privacyFooterLink}.`}
                        values={{
                            privacyFooterLink: (
                                <a className="cc-privacy-link" href={`/${localeID}/privacypolicy`} target="_blank" rel="noreferrer" data-cs-override="">
                                    {formatMessage({id: 'newsletter.registration.privacy.link', defaultMessage: 'Privacy Policy'})}
                                </a>
                            ),
                        }}
                    />
                </Box>
            </Flex>
        </Stack>
    )
}

type ObjType = {
    [key: string]: unknown
}

function formObject(obj: ObjType, excludeKeys: string[]): NewsletterFormValues {
    const newObject: NewsletterFormValues = {
        preferences: [],
        privacy: false,
        email: '',
        cu: localStorage.getItem('cu')!,
    }

    Object.entries(obj).forEach(([key, value]) => {
        if (excludeKeys.includes(key)) {
            if (key === 'name' || key === 'surname') {
                newObject[key] = value as string
            } else if (key === 'preferenceNewsletterConsent') {
                newObject.privacy = value === 'true' ? true : false
            }
        } else {
            if (value) {
                newObject.preferences.push(key[0].toUpperCase())
            }
        }
    })

    return newObject
}

export {NewsletterRegistrationStep2}
